import {
  CREATOR_SITE_THEME_PRESETS,
  type CreatorSiteThemePreset,
  type CreatorSiteThemeTokens,
  isCreatorSiteThemePreset,
  validateCreatorSiteThemePreset,
  validateCreatorSiteThemeTokens,
} from './creator-site';

export const CREATOR_SITE_BUTTON_STYLES = ['solid', 'outline', 'soft'] as const;
export type CreatorSiteButtonStyle = (typeof CREATOR_SITE_BUTTON_STYLES)[number];

export const CREATOR_SITE_THEME_PRESET_DEFINITIONS: Record<
  CreatorSiteThemePreset,
  CreatorSiteThemeTokens
> = {
  minimal: {
    primary: 'hsl(222 47% 11%)',
    background: 'hsl(0 0% 100%)',
    surface: 'hsl(210 20% 98%)',
    text: 'hsl(222 47% 11%)',
    radius: '0.5rem',
    buttonStyle: 'solid',
  },
  bold: {
    primary: 'hsl(262 83% 58%)',
    background: 'hsl(0 0% 100%)',
    surface: 'hsl(270 20% 98%)',
    text: 'hsl(224 71% 4%)',
    radius: '0.75rem',
    buttonStyle: 'solid',
  },
  editorial: {
    primary: 'hsl(24 10% 10%)',
    background: 'hsl(40 33% 98%)',
    surface: 'hsl(40 20% 94%)',
    text: 'hsl(24 10% 10%)',
    radius: '0.25rem',
    buttonStyle: 'outline',
  },
  coach: {
    primary: 'hsl(173 58% 39%)',
    background: 'hsl(160 20% 99%)',
    surface: 'hsl(160 16% 96%)',
    text: 'hsl(170 25% 15%)',
    radius: '0.625rem',
    buttonStyle: 'soft',
  },
  creator: {
    primary: 'hsl(346 77% 50%)',
    background: 'hsl(0 0% 100%)',
    surface: 'hsl(350 40% 98%)',
    text: 'hsl(222 47% 11%)',
    radius: '1rem',
    buttonStyle: 'solid',
  },
  dark: {
    primary: 'hsl(210 40% 98%)',
    background: 'hsl(222 47% 11%)',
    surface: 'hsl(217 33% 17%)',
    text: 'hsl(210 40% 98%)',
    radius: '0.5rem',
    buttonStyle: 'solid',
  },
};

export function listCreatorSiteThemePresets() {
  return CREATOR_SITE_THEME_PRESETS.map((preset) => ({
    preset,
    tokens: CREATOR_SITE_THEME_PRESET_DEFINITIONS[preset],
  }));
}

export function resolveCreatorSiteThemeTokens(
  preset: string,
  overrides?: CreatorSiteThemeTokens | null,
): CreatorSiteThemeTokens {
  const basePreset = isCreatorSiteThemePreset(preset) ? preset : 'minimal';
  const base = { ...CREATOR_SITE_THEME_PRESET_DEFINITIONS[basePreset] };
  if (!overrides) return base;
  return { ...base, ...overrides };
}

export function validateCreatorSiteThemeUpdate(input: {
  themePreset: string;
  themeTokens?: CreatorSiteThemeTokens | null;
}) {
  const presetResult = validateCreatorSiteThemePreset(input.themePreset);
  if (!presetResult.ok) return presetResult;

  if (input.themeTokens !== undefined) {
    const tokensResult = validateCreatorSiteThemeTokens(input.themeTokens ?? null);
    if (!tokensResult.ok) return tokensResult;
    if (tokensResult.tokens?.buttonStyle) {
      if (!(CREATOR_SITE_BUTTON_STYLES as readonly string[]).includes(tokensResult.tokens.buttonStyle)) {
        return { ok: false as const, message: 'buttonStyle must be solid, outline, or soft' };
      }
    }
  }

  return { ok: true as const, preset: presetResult.preset };
}
